import { FileInfo } from "@/lib/folder";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useIsApiReady } from "../hooks/use-api-ready";
import {
  KitFolderDto,
  PatchKitFolderBodyDto,
  PostKitFolderBodyDto,
  groupKitFolderControllerCreate,
  groupKitFolderControllerDelete,
  groupKitFolderControllerGetAll,
  groupKitFolderControllerUpdate,
} from "../lib/gen-api";
import { queryKeys } from "../lib/query-keys";

const buildPath = (
  folders: Record<string, KitFolderDto>,
  folderUuid: string,
) => {
  const pathSegments = [];
  let currentFolderUuid: string | null = folderUuid;

  while (currentFolderUuid) {
    const currentFolder: KitFolderDto | undefined =
      currentFolderUuid in folders ? folders[currentFolderUuid] : undefined;
    if (!currentFolder) break;

    pathSegments.unshift(currentFolder.name);
    currentFolderUuid = currentFolder.parentFolderUuid;
  }

  return pathSegments.join("/");
};

export const useKitFoldersData = (
  groupUuid: string | null,
  kitUuid: string | null,
) => {
  const queryClient = useQueryClient();

  const { data, isPending, error } = useQuery({
    enabled: useIsApiReady() && !!groupUuid && !!kitUuid,
    queryKey: queryKeys.kitFolders(groupUuid!, kitUuid!),
    queryFn: () =>
      groupKitFolderControllerGetAll({
        path: {
          groupUuid: groupUuid!,
          kitUuid: kitUuid!,
        },
      }).then((res) => {
        return res.data ?? null;
      }),
  });

  const { mutateAsync: createFolder, isPending: isCreatingFolder } =
    useMutation({
      mutationFn: (folderData: PostKitFolderBodyDto) => {
        if (!groupUuid || !kitUuid) {
          throw new Error("Group UUID and Kit UUID are required");
        }

        return groupKitFolderControllerCreate({
          path: {
            groupUuid,
            kitUuid,
          },
          body: folderData,
        }).then((res) => res.data);
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.kitFolders(groupUuid!, kitUuid!),
        });
      },
    });

  const { mutateAsync: updateFolder } = useMutation({
    mutationFn: (vars: PatchKitFolderBodyDto & { folderUuid: string }) => {
      if (!groupUuid || !kitUuid) {
        throw new Error("Group UUID and Kit UUID are required");
      }

      return groupKitFolderControllerUpdate({
        path: {
          groupUuid,
          kitUuid,
          folderUuid: vars.folderUuid,
        },
        body: {
          name: vars.name,
          description: vars.description,
        },
      });
    },
    onSettled: (_data, _error, vars) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.kitFolders(groupUuid!, kitUuid!),
      });

      queryClient.invalidateQueries({
        queryKey: queryKeys.kitFolder(groupUuid!, kitUuid!, vars.folderUuid),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.kitsFolder({
          kitUuid: kitUuid!,
          folderUuid: vars.folderUuid,
        }),
      });
    },
  });

  const { mutateAsync: deleteFolder } = useMutation({
    mutationFn: async (folderUuid: string) => {
      if (!groupUuid || !kitUuid) {
        throw new Error("Group UUID and Kit UUID are required");
      }

      const response = await groupKitFolderControllerDelete({
        path: {
          groupUuid,
          kitUuid,
          folderUuid,
        },
      });
      if (response.error) {
        throw new Error("Failed to delete folder");
      }
      return response.data;
    },
    onSettled: (_data, _error, folderUuid) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.kitFolder(groupUuid!, kitUuid!, folderUuid!),
      });

      queryClient.invalidateQueries({
        queryKey: queryKeys.kitFolders(groupUuid!, kitUuid!),
      });
    },
  });

  const files: FileInfo[] = data?.folders
    ? Object.values(data.folders)
        .map((folder) =>
          folder.files.map((file) => ({
            uuid: file.uuid,
            name: file.name,
            folderUuid: folder.uuid,
            folderPath: buildPath(data.folders, folder.uuid),
            groupName: folder.group?.name ?? null,
            dateDeleted: file.dateDeleted,
            deletedReason: file.deletedReason,
          })),
        )
        .flat()
    : [];

  const folders = useMemo(
    () => (data?.folders ? Object.values(data.folders) : []),
    [data],
  );

  return {
    folders,
    files: files,
    isPending,
    error,
    createFolder,
    updateFolder,
    deleteFolder,
    isCreatingFolder,
  };
};
